import React, { useCallback, useEffect, useState } from 'react';
import { TOOLBAR_BOX, TOOLBAR_BOX_OFF } from './ToolbarButton';

interface OpacityControlProps {
  /** 0–100, what the config stores. */
  value: number;
  onChange: (next: number) => void;
  disabled?: boolean;
}

const STEP = 5;
const clamp = (n: number) => Math.max(0, Math.min(100, Math.round(n)));

/**
 * The design element's opacity: the house slider and, beside it, the number
 * as an editable field — type a value, Enter or blur commits, ▲/▼ keys step
 * by 5 — the same manners as the font-size and gap boxes. One box of the
 * pill's height. The slider is live per tick, like a drag.
 */
const OpacityControl: React.FC<OpacityControlProps> = ({ value, onChange, disabled = false }) => {
  const shown = clamp(Number.isFinite(value) ? value : 100);
  const [draft, setDraft] = useState<string>(String(shown));
  useEffect(() => { setDraft(String(shown)); }, [shown]);

  const commit = useCallback(() => {
    const parsed = parseInt(draft, 10);
    if (Number.isFinite(parsed)) {
      const next = clamp(parsed);
      if (next !== shown) onChange(next);
      setDraft(String(next));
    } else {
      setDraft(String(shown));
    }
  }, [draft, shown, onChange]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowUp') { e.preventDefault(); onChange(clamp(shown + STEP)); }
    else if (e.key === 'ArrowDown') { e.preventDefault(); onChange(clamp(shown - STEP)); }
    else if (e.key === 'Enter') { e.preventDefault(); (e.target as HTMLInputElement).blur(); }
  };

  return (
    <div
      className={`${TOOLBAR_BOX} ${TOOLBAR_BOX_OFF} gap-1 pl-1.5 hover:bg-black/40 cursor-default overflow-hidden`}
      title={`Opacity: ${shown}%`}
      onMouseDown={(e) => e.stopPropagation()}
      data-design-opacity={shown}
    >
      <input
        type="range"
        min="0"
        max="100"
        value={shown}
        onChange={(e) => onChange(clamp(parseInt(e.target.value, 10)))}
        onClick={(e) => e.stopPropagation()}
        disabled={disabled}
        className="w-12 h-1 accent-blue-500 cursor-pointer disabled:opacity-40"
        aria-label="Opacity"
      />
      <input
        type="text"
        inputMode="numeric"
        value={draft}
        onChange={(e) => setDraft(e.target.value.replace(/[^0-9]/g, '').slice(0, 3))}
        onBlur={commit}
        onKeyDown={handleKeyDown}
        onClick={(e) => e.stopPropagation()}
        disabled={disabled}
        aria-label="Opacity value"
        className="w-7 h-full text-[11px] leading-none text-center bg-transparent text-white border-l border-white/20 tabular-nums focus:outline-none focus:bg-black/50 disabled:opacity-40"
        style={{ paddingInline: 0 }}
      />
    </div>
  );
};

export default OpacityControl;
